import { useEffect, useState } from 'react';
import {
  AlertCircle,
  AlertTriangle,
  Check,
  Loader2,
  Mail,
  RefreshCw,
  UserCog,
  Wrench,
  X,
} from 'lucide-react';
import type {
  EquipementCriticite,
  EquipementStatut,
  EquipementStatutAffectation,
} from '@mygmao/shared';
import { api, extractErrorMessage } from '../lib/api';
import { ModalPortal } from '../components/ModalPortal';
import { EquipementDetailDrawer } from '../components/equipements/EquipementDetailDrawer';
import { StatutEquipementBadge, CriticiteBadge } from '../components/equipements/badges';

/**
 * Page `/mes-affectations` (US-EQ-05).
 *
 *  - Liste des équipements dont je suis l'utilisateur affecté.
 *  - Tri : `en_attente_acceptation` en haut, puis `affecte`.
 *  - Pour chaque ligne en attente : boutons Accepter / Refuser (mini-modal).
 *  - Pour chaque ligne déjà affectée : bouton "Signaler un problème".
 *  - Click ligne → drawer 360°.
 */

interface AffectationRow {
  id: string;
  code_equipement: string;
  designation: string;
  statut: EquipementStatut;
  criticite: EquipementCriticite | null;
  statut_affectation: EquipementStatutAffectation;
  service: { id: string; nom_complet: string } | null;
  salle: { id: string; nom: string } | null;
  demandeur_affectation: {
    id: string;
    first_name: string;
    last_name: string;
  } | null;
  date_demande_affectation: string | null;
}

export function MesAffectationsPage() {
  const [items, setItems] = useState<AffectationRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [detailId, setDetailId] = useState<string | null>(null);
  const [respondTo, setRespondTo] = useState<{ row: AffectationRow; accept: boolean } | null>(null);
  const [reportingFor, setReportingFor] = useState<AffectationRow | null>(null);

  async function refresh() {
    setLoading(true);
    setErrorMessage(null);
    try {
      const res = await api.get<{ data: AffectationRow[] }>('/equipements/mes-affectations');
      setItems(res.data.data);
    } catch (err) {
      setErrorMessage(extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    refresh();
  }, []);

  const enAttente = items.filter((i) => i.statut_affectation === 'en_attente_acceptation');
  const affectes = items.filter((i) => i.statut_affectation === 'affecte');

  return (
    <div className="max-w-7xl mx-auto space-y-4">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center">
            <UserCog className="w-7 h-7 mr-2 text-primary-600" />
            Mes affectations
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            {items.length} équipement(s) ·{' '}
            {enAttente.length > 0
              ? `${enAttente.length} en attente d'acceptation`
              : 'aucune demande en attente'}
          </p>
        </div>
        <button
          onClick={refresh}
          className="inline-flex items-center p-2 border border-gray-300 rounded-lg text-gray-700 bg-white hover:bg-gray-50"
          title="Rafraîchir"
        >
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>

      {errorMessage && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 flex items-start space-x-2">
          <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-700">{errorMessage}</p>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center p-12">
          <Loader2 className="w-8 h-8 animate-spin text-primary-500" />
        </div>
      ) : items.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
          <Wrench className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-sm text-gray-500">
            Vous n'avez aucun équipement affecté à votre compte.
          </p>
        </div>
      ) : (
        <>
          {enAttente.length > 0 && (
            <section>
              <h2 className="text-sm font-semibold text-yellow-800 uppercase tracking-wide mb-2 flex items-center">
                <Mail className="w-4 h-4 mr-1.5" />
                Demandes en attente d'acceptation ({enAttente.length})
              </h2>
              <ul className="space-y-2">
                {enAttente.map((row) => (
                  <li key={row.id} className="rounded-lg border border-yellow-200 bg-yellow-50 p-3">
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                      <button
                        onClick={() => setDetailId(row.id)}
                        className="text-left min-w-0 flex-1"
                      >
                        <p className="text-sm font-medium text-gray-900 flex items-center flex-wrap gap-2">
                          <span className="text-primary-600 text-xs font-semibold uppercase">
                            {row.code_equipement}
                          </span>
                          {row.designation}
                          <StatutEquipementBadge statut={row.statut} />
                          {row.criticite && <CriticiteBadge criticite={row.criticite} />}
                        </p>
                        <p className="text-xs text-gray-600 mt-1">
                          {row.service?.nom_complet ?? 'Sans service'}
                          {row.salle && ` · ${row.salle.nom}`}
                        </p>
                        {row.demandeur_affectation && (
                          <p className="text-xs text-gray-500 mt-1">
                            Demandé par {row.demandeur_affectation.first_name}{' '}
                            {row.demandeur_affectation.last_name}
                            {row.date_demande_affectation &&
                              ` le ${new Date(row.date_demande_affectation).toLocaleString()}`}
                          </p>
                        )}
                      </button>
                      <div className="flex items-center space-x-2 flex-shrink-0">
                        <button
                          onClick={() => setRespondTo({ row, accept: true })}
                          className="inline-flex items-center px-3 py-1.5 bg-green-500 hover:bg-green-600 text-white rounded-lg text-sm font-medium"
                        >
                          <Check className="w-4 h-4 mr-1" />
                          Accepter
                        </button>
                        <button
                          onClick={() => setRespondTo({ row, accept: false })}
                          className="inline-flex items-center px-3 py-1.5 border border-red-300 text-red-700 hover:bg-red-50 rounded-lg text-sm font-medium"
                        >
                          <X className="w-4 h-4 mr-1" />
                          Refuser
                        </button>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {affectes.length > 0 && (
            <section>
              <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-2 flex items-center">
                <Wrench className="w-4 h-4 mr-1.5" />
                Équipements actifs ({affectes.length})
              </h2>
              <ul className="space-y-2">
                {affectes.map((row) => (
                  <li
                    key={row.id}
                    className="rounded-lg border border-gray-200 bg-white p-3 flex items-start justify-between gap-3 flex-wrap"
                  >
                    <button
                      onClick={() => setDetailId(row.id)}
                      className="text-left min-w-0 flex-1"
                    >
                      <p className="text-sm font-medium text-gray-900 flex items-center flex-wrap gap-2">
                        <span className="text-primary-600 text-xs font-semibold uppercase">
                          {row.code_equipement}
                        </span>
                        {row.designation}
                        <StatutEquipementBadge statut={row.statut} />
                        {row.criticite && <CriticiteBadge criticite={row.criticite} />}
                      </p>
                      <p className="text-xs text-gray-600 mt-1">
                        {row.service?.nom_complet ?? 'Sans service'}
                        {row.salle && ` · ${row.salle.nom}`}
                      </p>
                    </button>
                    <button
                      onClick={() => setReportingFor(row)}
                      className="inline-flex items-center px-3 py-1.5 border border-orange-300 text-orange-700 hover:bg-orange-50 rounded-lg text-sm font-medium"
                    >
                      <AlertTriangle className="w-4 h-4 mr-1" />
                      Signaler un problème
                    </button>
                  </li>
                ))}
              </ul>
            </section>
          )}
        </>
      )}

      {detailId && (
        <EquipementDetailDrawer
          equipementId={detailId}
          onClose={() => setDetailId(null)}
          onRefresh={refresh}
        />
      )}

      {respondTo && (
        <ResponseModal
          row={respondTo.row}
          accept={respondTo.accept}
          onClose={() => setRespondTo(null)}
          onDone={() => {
            setRespondTo(null);
            refresh();
          }}
        />
      )}

      {reportingFor && (
        <ReportProblemModal
          row={reportingFor}
          onClose={() => setReportingFor(null)}
          onDone={() => {
            setReportingFor(null);
            refresh();
          }}
        />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

function ResponseModal({
  row,
  accept,
  onClose,
  onDone,
}: {
  row: AffectationRow;
  accept: boolean;
  onClose(): void;
  onDone(): void;
}) {
  const [motif, setMotif] = useState('');
  const [commentaire, setCommentaire] = useState('');
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!accept && !motif.trim()) {
      setErrorMessage('Un motif de refus est obligatoire.');
      return;
    }
    setSaving(true);
    setErrorMessage(null);
    try {
      await api.post(`/equipements/${row.id}/repondre-affectation`, {
        accept,
        motif_refus: accept ? null : motif,
        commentaire: commentaire || null,
      });
      onDone();
    } catch (err) {
      setErrorMessage(extractErrorMessage(err));
    } finally {
      setSaving(false);
    }
  }

  return (
    <ModalPortal>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl max-w-md w-full shadow-2xl">
          <div className="flex items-center justify-between p-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">
              {accept ? "Accepter l'affectation" : "Refuser l'affectation"}
            </h3>
            <button
              onClick={onClose}
              className="p-1 rounded-lg hover:bg-gray-100"
              aria-label="Fermer"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>
          <form onSubmit={submit} className="p-4 space-y-3">
            <p className="text-sm text-gray-700">
              <span className="font-semibold">{row.code_equipement}</span> — {row.designation}
            </p>
            {errorMessage && (
              <div className="rounded-lg border border-red-200 bg-red-50 p-3 flex items-start space-x-2">
                <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-700">{errorMessage}</p>
              </div>
            )}
            {!accept && (
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Motif du refus *
                </label>
                <textarea
                  value={motif}
                  onChange={(e) => setMotif(e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  required
                />
              </div>
            )}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Commentaire (optionnel)
              </label>
              <textarea
                value={commentaire}
                onChange={(e) => setCommentaire(e.target.value)}
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
            <div className="flex justify-end space-x-2 pt-2">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                Annuler
              </button>
              <button
                type="submit"
                disabled={saving}
                className={`inline-flex items-center px-4 py-2 rounded-lg shadow-sm text-sm font-medium text-white disabled:opacity-50 ${
                  accept ? 'bg-green-500 hover:bg-green-600' : 'bg-red-500 hover:bg-red-600'
                }`}
              >
                {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                {accept ? 'Accepter' : 'Refuser'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </ModalPortal>
  );
}

function ReportProblemModal({
  row,
  onClose,
  onDone,
}: {
  row: AffectationRow;
  onClose(): void;
  onDone(): void;
}) {
  const [motif, setMotif] = useState('');
  const [commentaire, setCommentaire] = useState('');
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!motif.trim()) {
      setErrorMessage('Le motif est obligatoire.');
      return;
    }
    setSaving(true);
    setErrorMessage(null);
    try {
      await api.post(`/equipements/${row.id}/signaler-probleme`, {
        motif,
        commentaire: commentaire || null,
      });
      onDone();
    } catch (err) {
      setErrorMessage(extractErrorMessage(err));
    } finally {
      setSaving(false);
    }
  }

  return (
    <ModalPortal>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl max-w-md w-full shadow-2xl">
          <div className="flex items-center justify-between p-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center">
              <AlertTriangle className="w-4 h-4 mr-2 text-orange-500" />
              Signaler un problème
            </h3>
            <button
              onClick={onClose}
              className="p-1 rounded-lg hover:bg-gray-100"
              aria-label="Fermer"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>
          <form onSubmit={submit} className="p-4 space-y-3">
            <p className="text-sm text-gray-700">
              <span className="font-semibold">{row.code_equipement}</span> — {row.designation}
            </p>
            {errorMessage && (
              <div className="rounded-lg border border-red-200 bg-red-50 p-3 flex items-start space-x-2">
                <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-700">{errorMessage}</p>
              </div>
            )}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Motif *</label>
              <textarea
                value={motif}
                onChange={(e) => setMotif(e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                placeholder="Panne, dysfonctionnement, équipement absent, etc."
                required
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Commentaire (optionnel)
              </label>
              <textarea
                value={commentaire}
                onChange={(e) => setCommentaire(e.target.value)}
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
            <div className="flex justify-end space-x-2 pt-2">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                Annuler
              </button>
              <button
                type="submit"
                disabled={saving}
                className="inline-flex items-center px-4 py-2 rounded-lg shadow-sm text-sm font-medium text-white bg-orange-500 hover:bg-orange-600 disabled:opacity-50"
              >
                {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Signaler
              </button>
            </div>
          </form>
        </div>
      </div>
    </ModalPortal>
  );
}
